import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Sparkles, Heart, Zap, Star, Crown, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * AI Therapist Coach — a free chat companion for caregivers. The backend
 * stitches the child's tap history into the prompt so suggestions are
 * personalised, not generic.
 *
 * Hard rules (matched on the backend system prompt too):
 *   - never gives medical/diagnostic advice
 *   - replies are short, warm, evidence-based
 *   - always grounded in this specific child's data
 */

const STARTER_PROMPTS = [
  "How is my child doing this week?",
  "What new tiles should I add next?",
  "How do I help him use more variety?",
  "How do I model AAC during meals?",
  "When should I be concerned about progress?",
  "How do I get him to use longer phrases?",
];

export default function Coach() {
  const { profileId } = useParams();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]); // [{role:'user'|'assistant', text, snapshot?, followups?}]
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Credits & tier picker state (added 2026-05)
  const [tier, setTier] = useState("cheap");
  const [credits, setCredits] = useState(null); // {balance, free_trial_remaining}
  const [tierCosts, setTierCosts] = useState({ cheap: 1, standard: 5, premium: 10 });
  const [paywallOpen, setPaywallOpen] = useState(false);
  const scrollRef = useRef(null);

  // Load credits snapshot on mount so balance is shown before first message.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/credits/me");
        setCredits({ balance: data.balance, free_trial_remaining: data.free_trial_remaining });
        if (data.tier_costs) setTierCosts(data.tier_costs);
      } catch (_e) { /* fail-open: backend will still enforce */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/profiles/${profileId}`);
        setProfile(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        if (e.response?.status === 404) nav("/profiles");
      }
    })();
  }, [profileId, nav]);

  // Greeting message — only on first load with profile
  useEffect(() => {
    if (!profile || messages.length > 0) return;
    setMessages([
      {
        id: `m-${Date.now()}-greet`,
        role: "assistant",
        text:
          `Hi! I'm SonSpeak Coach — an AAC strategy buddy. I can see ${profile.name}'s ` +
          `tap history and suggest what to model next. Ask me anything, or pick a starter below. ` +
          `(I'm not a replacement for a licensed SLP — for medical or diagnostic questions ` +
          `please reach out to your therapist.)`,
        followups: STARTER_PROMPTS.slice(0, 3),
      },
    ]);
  }, [profile, messages.length]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    // Phase 4 — soft client-side scan for child-identifying tokens before
    // we ship the message to the LLM. Catches the most common foot-guns
    // (proper noun + age, common diagnosis acronyms). Not a hard block —
    // parent can dismiss and continue if they understand the tradeoff.
    const PII_PATTERNS = [
      /\b(autism|autistic|asd|adhd|down syndrome|cerebral palsy|apraxia)\b/i,
      /\b[A-Z][a-z]{2,}\b.*\b(age|years? old|yo|months? old)\b.*\d+/i,
      /\b\d+\s*(year|yr|month|mo)s?[\s-]*old\b/i,
      /\bdiagnos(?:is|ed)\b/i,
      /\b(elementary|middle school|high school|kindergarten|preschool)\b.*\b[A-Z][a-z]+\b/,
    ];
    const triggered = PII_PATTERNS.find((re) => re.test(text));
    if (triggered && !textArg) {  // skip if this is a re-send after warning
      const ok = window.confirm(
        "Heads up: it looks like your message may include your child's name, " +
        "age, or diagnosis. SonSpeak's privacy promise is to keep child info " +
        "off our servers — and the AI Coach gives better generic advice without " +
        "those details.\n\nContinue anyway?"
      );
      if (!ok) return;
    }
    setInput("");
    setError("");
    const userMsg = { id: `m-${Date.now()}-u`, role: "user", text };
    const next = [...messages, userMsg];
    setMessages(next);
    setBusy(true);
    try {
      const { data } = await api.post("/coach/chat", {
        profile_id: profileId,
        message: text,
        history: next.slice(-10).map((m) => ({ role: m.role, text: m.text })),
        tier,
      });

      // Hard paywall — user has 0 credits, backend declined to call the LLM.
      if (data.ok === false && data.out_of_credits) {
        setCredits(data.credits || credits);
        setPaywallOpen(true);
        // Drop the user message we just appended since it never got a reply.
        setMessages((prev) => prev.slice(0, -1));
        setInput(text); // give them their typing back
        setBusy(false);
        return;
      }

      // Sync credits cache from the response so the badge stays accurate.
      if (data.credits) setCredits(data.credits);

      setMessages((prev) => [
        ...prev,
        {
          id: `m-${Date.now()}-a`,
          role: "assistant",
          text: data.reply || "(no reply)",
          followups: data.followups || [],
          snapshot: data.snapshot || null,
          tier_used: data.tier_used || null,
          safety_block: data.safety_block || null,
        },
      ]);
    } catch (e) {
      // Starter tier (HTTP 402) is already handled by the global
      // TierUpgradeModal listener — just surface a friendly inline.
      if (e.response?.status === 402) {
        setMessages((prev) => prev.slice(0, -1));
        setInput(text);
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
    setBusy(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="coach-page">
      <header className="px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3">
        <Link to={`/board/${profileId}`} className="pill-btn" data-testid="coach-back">
          <ArrowLeft size={16} />
          <span className="hidden sm:inline">Back</span>
        </Link>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-10 h-10 rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-2xl shrink-0 bg-[#A78BFA]">
            <Sparkles size={20} className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="display text-base sm:text-xl font-extrabold leading-tight">
              SonSpeak Coach
            </div>
            <div className="text-xs text-[color:var(--ink-soft)] hidden sm:flex items-center gap-1">
              <Heart size={11} className="fill-[color:var(--coral)] text-[color:var(--coral)]" />
              AI strategy buddy{profile ? ` · reading ${profile.name}'s board` : ""}
            </div>
          </div>
        </div>
        <div className="flex-1" />
        {credits && (
          <CreditBadge
            balance={credits.balance}
            trial={credits.free_trial_remaining}
          />
        )}
      </header>

      <main
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 max-w-3xl w-full mx-auto"
        data-testid="coach-scroll"
      >
        {messages.map((m, i) => (
          <div
            key={m.id || `msg-${i}`}
            className={`mb-4 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`coach-msg-${m.role}-${i}`}
          >
            <div
              className={`brutal-card p-4 max-w-[85%] whitespace-pre-wrap leading-relaxed ${
                m.role === "user"
                  ? "bg-[color:var(--gold)]"
                  : "bg-white"
              }`}
            >
              {m.text}
              {m.snapshot && (
                <div className="mt-3 pt-3 border-t-[2px] border-[color:var(--paper-muted)] flex flex-wrap gap-3 text-xs text-[color:var(--ink-soft)]">
                  <span><strong>{m.snapshot.total_taps_14d ?? 0}</strong> taps in 14d</span>
                  <span><strong>{m.snapshot.unique_words_14d ?? 0}</strong> unique words</span>
                  <span><strong>{m.snapshot.days_active_14d ?? 0}</strong> active days</span>
                </div>
              )}
              {m.tier_used?.downgraded && m.role === "assistant" && (
                <div className="mt-2 text-xs text-[color:var(--ink-soft)] flex items-center gap-1.5" data-testid={`coach-downgrade-${i}`}>
                  <AlertCircle size={11} />
                  Saved your premium credits — used Fast this turn.
                </div>
              )}
              {m.followups && m.followups.length > 0 && m.role === "assistant" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {m.followups.map((q, j) => (
                    <button
                      key={j}
                      type="button"
                      onClick={() => send(q)}
                      className="pill-btn text-sm"
                      disabled={busy}
                      data-testid={`coach-followup-${i}-${j}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {busy && (
          <div className="mb-4 flex justify-start" data-testid="coach-typing">
            <div className="brutal-card p-4 bg-white inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[color:var(--ink)] animate-bounce" />
              <span className="w-2 h-2 rounded-full bg-[color:var(--ink)] animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="w-2 h-2 rounded-full bg-[color:var(--ink)] animate-bounce" style={{ animationDelay: "240ms" }} />
              <span className="text-sm text-[color:var(--ink-soft)] ml-1">Coach is thinking…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="brutal-card p-3 bg-[#FFE3E0] text-[color:var(--coral-deep)] text-sm" data-testid="coach-error">
            {error}
          </div>
        )}
      </main>

      <footer className="border-t-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] px-4 sm:px-6 py-3">
        <div className="max-w-3xl mx-auto">
          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="coach-starters">
              {STARTER_PROMPTS.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => send(q)}
                  className="pill-btn text-sm"
                  disabled={busy}
                  data-testid={`coach-starter-${i}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          <TierPicker tier={tier} setTier={setTier} costs={tierCosts} trial={credits?.free_trial_remaining || 0} />
          <div className="flex gap-2">
            <textarea
              className="brutal-input flex-1 resize-none"
              rows={2}
              placeholder="Ask the coach anything… (Shift+Enter for new line)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy}
              data-testid="coach-input"
            />
            <button
              type="button"
              className="pill-btn primary"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="coach-send"
              aria-label="Send message"
            >
              <Send size={18} />
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
          <div className="mt-2 text-[11px] text-[color:var(--ink-soft)]">
            SonSpeak Coach is an AI assistant, not a licensed therapist. For
            medical or diagnostic concerns please contact your SLP or pediatrician.
          </div>
          <div
            className="mt-1 brutal-card bg-amber-50 p-2 text-[11px] text-amber-900 leading-snug"
            data-testid="coach-privacy-warning"
          >
            <strong>Privacy:</strong> please don't enter your child's full name,
            date of birth, diagnosis, school name, or other identifying details.
            The coach works best with general descriptions like "my child uses
            AAC and needs help with morning routines."
          </div>
        </div>
      </footer>
      {paywallOpen && (
        <PaywallModal
          profileId={profileId}
          onClose={() => setPaywallOpen(false)}
        />
      )}
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function CreditBadge({ balance, trial }) {
  const total = (balance || 0) + (trial || 0);
  const tone = total === 0 ? "text-[color:var(--coral-deep)]" : "text-[color:var(--ink)]";
  return (
    <div
      className={`hidden sm:flex flex-col items-end text-right ${tone}`}
      data-testid="coach-credit-badge"
      title="Credits are used by AI Coach only. Your board, voice, and photos always work."
    >
      <div className="text-xs uppercase tracking-wider font-bold opacity-70">Credits</div>
      <div className="text-base font-extrabold tabular-nums" data-testid="coach-credit-total">{total}</div>
      {trial > 0 && (
        <div className="text-[10px] text-[color:var(--brand-blue,#2563EB)] font-bold" data-testid="coach-credit-trial">
          {trial} trial · Fast only
        </div>
      )}
    </div>
  );
}

const TIER_META = [
  { id: "cheap",    label: "Fast",     Icon: Zap,   color: "var(--brand-blue, #2563EB)" },
  { id: "standard", label: "Standard", Icon: Star,  color: "var(--brand-teal, #1E879D)" },
  { id: "premium",  label: "Premium",  Icon: Crown, color: "var(--coral, #E63946)" },
];

function TierPicker({ tier, setTier, costs, trial }) {
  const trialForcesCheap = trial > 0;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="coach-tier-picker">
      {TIER_META.map(({ id, label, Icon, color }) => {
        const active = tier === id;
        const lockedByTrial = trialForcesCheap && id !== "cheap";
        return (
          <button
            key={id}
            type="button"
            onClick={() => !lockedByTrial && setTier(id)}
            className={`pill-btn text-xs ${active ? "gold" : ""} ${lockedByTrial ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={lockedByTrial}
            aria-pressed={active}
            data-testid={`coach-tier-${id}`}
            title={lockedByTrial ? "Granted credits use the Fast tier. Other tiers unlock once they're used." : `${costs[id]} credit${costs[id] === 1 ? "" : "s"} per message`}
          >
            <Icon size={12} style={{ color }} /> {label} · {costs[id]}cr
          </button>
        );
      })}
      <span className="text-[11px] text-[color:var(--ink-soft)] ml-auto">
        Credits power Coach AI. Your board always works — no credits needed.
      </span>
    </div>
  );
}

function PaywallModal({ profileId, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="coach-paywall-modal"
    >
      <div
        className="brutal-card bg-white p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-5xl mb-3" aria-hidden>💛</div>
        <h2 className="display text-2xl font-extrabold mb-2">Your board still works.</h2>
        <p className="text-[color:var(--ink-soft)] leading-relaxed mb-4">
          Your AAC board, voice, photos, and offline mode are unaffected.
          Only the AI Coach is paused — it runs on credits because AI calls
          have a real cost, and we'd rather charge for them honestly than
          raise the subscription price.
        </p>
        <div className="flex flex-col gap-2">
          <Link
            to={`/board/${profileId}`}
            className="pill-btn primary justify-center"
            onClick={onClose}
            data-testid="coach-paywall-go-to-board"
          >
            Go to my board →
          </Link>
          <button
            type="button"
            className="pill-btn justify-center opacity-60 cursor-not-allowed"
            disabled
            title="Top-ups will go live once paid billing is available."
            data-testid="coach-paywall-topup"
          >
            Top up — coming soon
          </button>
          <button
            type="button"
            className="text-xs text-[color:var(--ink-soft)] underline mt-1"
            onClick={onClose}
            data-testid="coach-paywall-close"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
